import { ConfigObj, PubSub, MediaQuery } from '../../utils/'

const defaultOpts = {
  iconMark: '?',
  title: '',
  content: '[content here]'
}

const PREFIX = 'ps-'
const MODULE_NAME = 'tooltip'

// Custom event to be registered, emitted and listened to
const EVENTS = {
  close: `${MODULE_NAME}:close`
}

// Scoped CSS selectors used for DOM manipulation and styling
const SELECTORS = {
  tooltip: PREFIX + MODULE_NAME,
  icon: `${PREFIX + MODULE_NAME}__icon`,
  title: `${PREFIX + MODULE_NAME}__title`,
  bubble: `${PREFIX + MODULE_NAME}__bubble`,
  close: `${PREFIX + MODULE_NAME}__close`,
  arrow: `${PREFIX + MODULE_NAME}__arrow`
}

/**
 * Tooltip class - Genereates tooltip around a form field/label structure
 * @param {HTMLElement} $el - the element to which the class and its listeners are applied
 * @param {Object} opts - The user options to overwrite the default opts
 */
export default class Tooltip {

  constructor($el, opts) {
    this.$el = $el
    this.opts = ConfigObj.merge(defaultOpts, opts)
    this.isOpen = false
  }

  init() {
    this.$icon = this.createIcon()
    this.$el.appendChild(this.$icon)

    this.$bubble = this.createBubble()

    this.$el.classList.add(SELECTORS.tooltip)

    this.addEventListeners()
  }

  /**
  * Generates the clickable icon DOM node
  * @returns {HTMLElement}
  */
  createIcon() {
    const icon = document.createElement('span')
    icon.classList.add(SELECTORS.icon)
    icon.innerText = this.opts.iconMark

    return icon
  }

  /**
  * Generates the tooltip bubble DOM node
  * @returns {HTMLElement}
  */
  createBubble() {
    const bubble = document.createElement('span')

    const innerMarkup = []
    innerMarkup.push(`<span class="${SELECTORS.close}"></span>`)
    innerMarkup.push(`<span class="${SELECTORS.arrow}"></span>`)
    if (this.opts.title) innerMarkup.push(`<span class="${SELECTORS.title}">${this.opts.title}</span>`)
    innerMarkup.push(this.opts.content)

    bubble.classList.add(SELECTORS.bubble)
    bubble.innerHTML = innerMarkup.join('')

    return bubble
  }

  /**
  * Calculates and sets the arrow position relative to the icons centeral axis
  */
  setArrowPosition() {
    // TODO: Set media query object on orientation change and load
    const $arrow = MediaQuery.create({ maxWidth: 767 }).matches
      ? this.$el.parentNode.querySelector(`.${SELECTORS.arrow}`)
      : this.$el.querySelector(`.${SELECTORS.arrow}`)

    const arrowDimensions = $arrow.getClientRects()[0]

    const iconDimensions = this.$icon.getClientRects()[0]
    // Get the true center position relative to the arrow icon
    // Arbitrary + 2 here for border width (border-width property is split in 2 props in FF and i don't want to write that verbose code to get and add them)
    const iconOffset = (iconDimensions.width - arrowDimensions.width) + 2

    const bubbleDimensions = this.$bubble.getClientRects()[0]

    $arrow.style.left = `${iconDimensions.left - bubbleDimensions.left + iconOffset}px`
  }

  /**
  * Toggles tooltip bubble existance in DOM
  * @param {Object} e - The event data of the referencing DOM Element
  */
  showToolTip(e) {
    if (this.isOpen) return this.hideToolTip(e)

    // Trigger our custom event to close all other instances of the tooltips
    PubSub.publish(EVENTS.close, e)

    this.isOpen = true
    // Set the location of the bubble in the DOM according to the device size
    MediaQuery.create({ maxWidth: 767 }).matches
      ? this.$el.parentNode.insertBefore(this.$bubble, this.$el.nextSibling)
      : this.$el.appendChild(this.$bubble)

    this.setArrowPosition()

    // Prevent tooltip trigger from inheriting the parent click events
    e.stopPropagation()
    e.preventDefault()
  }

  /**
  * Toggles tooltip bubble existance in DOM
  * @param {Object} e - The event data of the referencing DOM Element
  */
  hideToolTip(e) {
    if (!this.isOpen) return

    this.isOpen = false
    this.$bubble.parentNode.removeChild(this.$bubble)

    if (!e) return
    // Prevent tooltip trigger from inheriting the parent click events
    e.stopPropagation()
    e.preventDefault()
  }

  /**
  * Adds basic keyboard accessibility
  */
  onKeyEntry(e) {
    // User hit the escape key
    // Regex here for full browser support - IE emits 'Esc' while other browsers emit 'Escape' as the key and is more readable than 27 (e.which)
    if (/esc(?:ape)*/i.test(e.key)) {
      this.hideToolTip()
    }
  }

  onOuterClick(e) {
    const regex = RegExp(`${MODULE_NAME}__[a-z]+`, 'g')
    // If the clicked element className is not a child of tooltip eg tooltip_child
    if (!regex.test(e.target.classList.value) && this.isOpen) return this.hideToolTip()
  }

  /**
  * Adds required event listeners to handle interactions
  */
  addEventListeners() {
    PubSub.subscribe(EVENTS.close, this.hideToolTip.bind(this))
    // The tooltip needs to be recreated at different breakpoints so destroying it on orientation change
    window.addEventListener('orientationchange', this.hideToolTip.bind(this))
    document.addEventListener('click', this.onOuterClick.bind(this))
    this.$icon.addEventListener('click', this.showToolTip.bind(this))
    this.$el.addEventListener('click', this.hideToolTip.bind(this))
    this.$el.addEventListener('keyup', this.hideToolTip.bind(this))
    this.$bubble.addEventListener('touchend', this.hideToolTip.bind(this))
  }
}



// WEBPACK FOOTER //
// ./js/self-serve/modules/Tooltip.js